import { render } from 'engine/index';
import type { FlikkenState } from 'shared/modelTypes/flikken-challenge';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import { CheckboxField, ToggleField } from './FormComponents';

export function PlayFlikken({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				//
				if (data.kind === 'update-state') {
					const state = data.state as FlikkenState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: FlikkenState) => {
		const s = state.stage;
		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={() => {
							console.log('start the game');
							rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'start-game' } });
						}}
						state={state}
					/>,
				);
				break;
			case 'choose-theme':
				render(
					view,
					<div>
						<h3>Thema aan het kiezen</h3>
						<p>Wacht op spelers</p>
						<Button
							on:click={() => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'next-screen' } });
							}}
						>
							Direct doorgaan
						</Button>
					</div>,
				);
				break;
			case 'play-rounds':
				render(
					view,
					<div>
						<h3>Playing round {(state.roundIndex ?? 0) + 1}</h3> <p>let players make their moves.</p>
						<hr class="dark:border-gray-900" />
						<PlayerTable state={state} sessionId={sessionId} />
						<div class="size-8"></div>
						<ToggleField
							compact
							name="lastRound"
							label="Laatste ronde huidige rechercheur"
							value={state.lastRound}
							onChange={(v) => {
								const lastRound = v || false;
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'set-is-last-round', lastRound } });
							}}
						/>
						<div class="size-8"></div>
						<Button
							on:click={() => {
								rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'force-restart-round' } });
							}}
						>
							Forceer nieuwe ronde start
						</Button>
					</div>,
				);
				break;
			case 'play-endgame':
				{
					// const reports = generatePlayerReports();
					render(
						view,
						<div>
							playing Ending game.
							<PlayerTable state={state} sessionId={sessionId} />
							{/* <pre>{JSON.stringify(reports, null, 2)}</pre> */}
							{!!state.players.find((p) => p.lastScreen.kind === 'show-presentgame-screen') && (
								<Button
									on:click={() => {
										rpc.sendFlikkenMessage.query({ sessionId, update: { kind: 'next-screen' } });
									}}
								>
									Volgende
								</Button>
							)}
						</div>,
					);
				}

				break;
			case 'ending-report':
				render(
					view,
					<div>
						showing end report.
						<PlayerTable state={state} sessionId={sessionId} />
					</div>,
				);
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });
		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({ state, onContinue }: { state: FlikkenState; onContinue: () => void }) {
	console.log('render players', state.players);

	const playersWithName = state.players.filter((p) => p.name);

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Color</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName.map((player) => {
							return (
								<TrBody>
									<Td>{player.name}</Td>
									<Td>{player.color}</Td>
									<Td>{player.id}</Td>
								</TrBody>
							);
						})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			<div class="mt-5"></div>
			{state.players.length > 0 && <Button on:click={onContinue}>Doorgaan</Button>}
		</div>
	);
}

function PlayerTable({ sessionId, state }: { sessionId: string; state: FlikkenState }) {
	return (
		<>
			<h3 class="mb-2 mt-5 text-lg font-bold text-gray-900 dark:text-gray-200">Players:</h3>
			<Table>
				<thead>
					<TrHead>
						<Th title="When disabled the players turn will always be skipped">Active</Th>
						<Th>Name</Th>
						<Th>Color</Th>
						<Th>Score</Th>
						<Th>Theme</Th>
						<Th>Id</Th>
					</TrHead>
				</thead>
				<tbody>
					{state.players.map((player) => {
						return (
							<TrBody>
								<Td>
									<CheckboxField
										name={player.id + '-active'}
										label={''}
										value={player.active}
										onChange={(v) => {
											rpc.sendFlikkenMessage.query({
												sessionId,
												update: { kind: 'set-player-active', playerId: player.id, active: v ?? true },
											});
										}}
									/>
								</Td>
								<Td>{player.name}</Td>
								<Td>{player.color}</Td>
								<Td>{player.score}</Td>

								<Td>{player.theme.title}</Td>
								<Td>{player.id}</Td>
							</TrBody>
						);
					})}

					<TrBody>
						<Td></Td>
						<Td>
							<strong>Totaal:</strong>
						</Td>
						<Td>{state.players.reduce((acc, cur) => cur.score + acc, 0)}</Td>

						<Td></Td>
						<Td></Td>
						<Td></Td>
					</TrBody>
				</tbody>
			</Table>
		</>
	);
}
